import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { RegionPage } from "./region.$key";
import { listClubsByRegion, regionFacts, regionPhoto } from "../lib/api/lidoo.functions";
import { DICTS, fill, regionName, LOCALIZED_LANGS, type Lang } from "../lib/i18n";
import { isIndexableLocale, NOINDEX_META, regionIsIndexable } from "../lib/indexing";
import { catalogueHreflang } from "../lib/seo";

export const Route = createFileRoute("/$locale/region/$key")({
  beforeLoad: ({ params }) => {
    if (params.locale === "en") throw redirect({ to: "/region/$key", params: { key: params.key } });
    if (!LOCALIZED_LANGS.includes(params.locale as Lang)) throw notFound();
  },
  loader: async ({ params }) => await Promise.all([
      listClubsByRegion({ data: { regionKey: params.key } }),
      regionFacts({ data: { regionKey: params.key } }),
      regionPhoto({ data: { regionKey: params.key } }),
    ]).then(([list, facts, hero]) => {
      // Same gate as the English route: an empty region is a 404, not a page.
      if (list.clubs.length === 0) throw notFound();
      return { ...list, facts, hero };
    }),
  head: (ctx) => {
    const d = DICTS[ctx.params.locale as Lang];
    const stored = ctx.loaderData?.regionLabel ?? "";
    const label = regionName(ctx.params.key, stored, (k) => d[k] ?? k);
    const count = ctx.loaderData?.clubs.length ?? 0;
    return {
      meta: [
        { title: `${fill(d.region_meta_title, { place: label })} | sunbedly` },
        {
          name: "description",
          content: fill(d.region_meta_desc, { place: label, n: String(count) }),
        },
        // Same threshold as the English route, plus the language gate: the
        // club cards on this page carry English text, so a Dutch, German or
        // French region page is a translated heading over an English list.
        ...(isIndexableLocale(ctx.params.locale) && regionIsIndexable(count) ? [] : [NOINDEX_META]),
      ],
      links: catalogueHreflang(`/region/${ctx.params.key}`, ctx.params.locale),
    };
  },
  component: () => <RegionPage {...Route.useLoaderData()} />,
});
